import { useState, useCallback, useEffect, useRef } from "react";
import { fetchTikTokVideos } from "@/services/tiktokApi";
import { TikTokVideo } from "@/types/api";
import { toast } from "sonner";
import { useAutoFetchState } from "./useAutoFetchState";
import { useUrlValidation } from "./useUrlValidation";
import { useOnlineStatus } from "./useOnlineStatus";
import { useAutoVideoFetch } from "./useAutoVideoFetch";

export function useVideoFetch() {
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [videos, setVideos] = useState<TikTokVideo[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentCursor, setCurrentCursor] = useState("0");
  const [hasMore, setHasMore] = useState(true);
  const [attempts, setAttempts] = useState(0);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const manualFetchInitiated = useRef(false);

  const isOnline = useOnlineStatus();
  const { validateTikTokUrl } = useUrlValidation();
  const autoFetchState = useAutoFetchState();

  useEffect(() => {
    autoFetchState.autoFetchingRef.current = autoFetchState.isAutoFetching;
  }, [autoFetchState.isAutoFetching]);

  const fetchVideos = useCallback(async (currentUrl: string, cursor: string = "0", append: boolean = false) => {
    if (!currentUrl.trim()) {
      setErrorMessage("Please enter a TikTok URL");
      return null;
    }

    if (!validateTikTokUrl(currentUrl)) {
      setErrorMessage("Invalid TikTok collection URL format. Example: https://www.tiktok.com/@username/collection/collection-id");
      return null;
    }

    const loadingState = cursor === "0" ? setIsLoading : setIsFetchingMore;
    loadingState(true);
    setErrorMessage(null);
    
    try {
      const response = await fetchTikTokVideos(currentUrl, cursor);
      
      if (response.videos && response.videos.length > 0) {
        const newVideos = response.videos;
        setVideos(prev => append ? [...prev, ...newVideos] : newVideos);
        setShowResults(true);
        setCurrentCursor(response.next_cursor);
        setHasMore(response.hasMore !== false && response.next_cursor !== "0");
        
        autoFetchState.setFetchedPagesCount(prev => prev + 1);
        autoFetchState.setTotalVideosFetched(prev => append ? prev + newVideos.length : newVideos.length);
        
        return {
          success: true,
          nextCursor: response.next_cursor,
          hasMore: response.hasMore !== false && response.next_cursor !== "0",
          videosCount: newVideos.length
        };
      } else {
        if (cursor === "0") {
          throw new Error("No videos found in this collection. Please check the URL and try again.");
        } else {
          setHasMore(false);
          return {
            success: true,
            nextCursor: "0",
            hasMore: false,
            videosCount: 0
          };
        }
      }
    } catch (error) {
      console.error('Error in fetch:', error);
      const message = error instanceof Error ? error.message : "Failed to fetch videos";
      
      if (cursor === "0") {
        setErrorMessage(message);
        setShowResults(false);
      }
      
      return {
        success: false,
        error: message
      };
    } finally {
      loadingState(false);
    }
  }, [validateTikTokUrl, autoFetchState]);

  const { startAutoFetch, continueAutoFetch } = useAutoVideoFetch({
    isLoading,
    isFetchingMore,
    url,
    isOnline,
    autoFetchState,
    setVideos,
    setCurrentCursor,
    fetchVideos
  });

  useEffect(() => {
    const shouldAutoFetch = 
      url && 
      isOnline && 
      !isLoading && 
      !autoFetchState.isAutoFetching && 
      !(autoFetchState.fetchErrored && autoFetchState.lastFetchedUrl === url) && 
      (manualFetchInitiated.current || autoFetchState.lastFetchedUrl !== url);
    
    if (shouldAutoFetch) {
      manualFetchInitiated.current = false;
      startAutoFetch();
    }
  }, [url, isOnline, isLoading, autoFetchState.isAutoFetching, autoFetchState.fetchErrored, 
      autoFetchState.lastFetchedUrl, startAutoFetch]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submitted with URL:", url);

    if (typeof window !== "undefined" && url) {
      (window as any).lastTikTokCollectionUrl = url;
    }

    autoFetchState.setFetchErrored(false);
    manualFetchInitiated.current = true;
    
    setVideos([]);
    setCurrentCursor("0");
    setHasMore(true);
    autoFetchState.setFetchedPagesCount(0);
    autoFetchState.setTotalVideosFetched(0);
    fetchVideos(url, "0", false);
  }, [url, fetchVideos, autoFetchState]);

  const handleLoadMore = useCallback(() => {
    if (!isLoading && !isFetchingMore && hasMore) {
      fetchVideos(url, currentCursor, true);
    }
  }, [url, currentCursor, hasMore, isLoading, isFetchingMore, fetchVideos]);

  const handleRetry = useCallback(() => {
    autoFetchState.setFetchErrored(false);
    manualFetchInitiated.current = true;
    fetchVideos(url, "0", false);
  }, [url, fetchVideos, autoFetchState]);

  return {
    url,
    setUrl,
    isLoading,
    isFetchingMore,
    videos,
    showResults,
    errorMessage,
    isOnline,
    hasMore,
    handleSubmit,
    handleRetry,
    handleLoadMore,
    isAutoFetching: autoFetchState.isAutoFetching,
    autoFetchPaused: autoFetchState.autoFetchPaused,
    fetchedPagesCount: autoFetchState.fetchedPagesCount,
    totalVideosFetched: autoFetchState.totalVideosFetched,
    startAutoFetch,
    pauseAutoFetch: autoFetchState.pauseAutoFetch,
    resumeAutoFetch: autoFetchState.resumeAutoFetch,
    cancelAutoFetch: autoFetchState.cancelAutoFetch
  };
}
